/**
 * EBlast (email) campaigns list — mirrored off the RTB campaigns list, sourced from `email_campaigns`.
 *
 * Two views over the same filters (status chips, deploy-date window, client multi-select):
 *   - Group by SO (default): one row per SO with its drops in an accordion; each drop links to its
 *     detail page, and a Report button opens the eblast report builder for the SO.
 *   - Flat: one row per drop, via the shared DataTable.
 *
 * "Client" here is the owning AGENCY — email campaigns carry no client_id, only `agency_info`.
 */

import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable, type Column } from '../components/DataTable';
import { email, type ClientOption, type EmailCampaign, type EmailSoGroup, type ListParams, type Page } from '../lib/api';
import { stripApiPrefix } from '../lib/eblast-title';

const int = (n: number) => (n || 0).toLocaleString('en-US');
const pct = (n: number) => `${(Number(n) || 0).toFixed(2)}%`;
const day = (v: string | null) => (v ? v.slice(0, 10) : '—');

/** The filters shared by both views. */
interface Filters { status?: string; startDate?: string; endDate?: string; clientIds: string[] }

/** A count with its rate underneath — the same stacked figure the RTB list uses. */
function Stat({ value, rate }: { value: number; rate: string }) {
  return (
    <span className="stat">
      <span className="stat-value num">{int(value)}</span>
      <span className="stat-share num">{rate}</span>
    </span>
  );
}

function StatusPill({ status }: { status: string }) {
  const s = status.trim().toUpperCase();
  // Completed reads red (like the RTB status flags); active green, paused amber, everything else neutral.
  const tone = s === 'COMPLETED' ? 'done' : s === 'ACTIVE' ? 'ok' : s === 'PAUSED' ? 'warn' : 'off';
  return <span className={`pill ${tone}`}>{status || '—'}</span>;
}

const STATUSES = ['all', 'PENDING', 'ACTIVE', 'COMPLETED'] as const;
type StatusFilter = (typeof STATUSES)[number];

export function EblastCampaignsScreen() {
  const [grouped, setGrouped] = useState(true);
  const [status, setStatus] = useState<StatusFilter>('all');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [clientIds, setClientIds] = useState<string[]>([]);
  const [clientOptions, setClientOptions] = useState<ClientOption[]>([]);

  const filters: Filters = {
    status: status !== 'all' ? status : undefined,
    startDate: start || undefined,
    endDate: end || undefined,
    clientIds,
  };
  // Serialize for effect deps (arrays/objects are unstable by identity).
  const filterKey = `${status}|${start}|${end}|${clientIds.join(',')}`;

  // Client options reflect the status/date filter (not the already-picked clients) — like the RTB page.
  useEffect(() => {
    let live = true;
    email.clients({ status: filters.status, startDate: filters.startDate, endDate: filters.endDate })
      .then((opts) => { if (live) setClientOptions(opts); })
      .catch(() => { if (live) setClientOptions([]); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, start, end]);

  // Drop any selected client that is no longer among the options after a filter change.
  useEffect(() => {
    setClientIds((ids) => ids.filter((id) => clientOptions.some((o) => o.id === id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientOptions]);

  return (
    <>
      <div className="filter-bar">
        <span className="filter-label">Status</span>
        {STATUSES.map((s) => (
          <button key={s} className={`chip${status === s ? ' chip-on' : ''}`} onClick={() => setStatus(s)}>
            {s === 'all' ? 'All' : s.charAt(0) + s.slice(1).toLowerCase()}
          </button>
        ))}
        <span className="filter-label" style={{ marginLeft: '1rem' }}>Deploy</span>
        <input className="filter-date" type="date" value={start} onChange={(e) => setStart(e.target.value)} aria-label="From" />
        <span className="muted">to</span>
        <input className="filter-date" type="date" value={end} onChange={(e) => setEnd(e.target.value)} aria-label="To" />
        {(start || end) && <button className="chip" onClick={() => { setStart(''); setEnd(''); }}>Clear</button>}
        <ClientMultiSelect options={clientOptions} value={clientIds} onChange={setClientIds} />
        <span className="grow" />
        <label className="toggle">
          <input type="checkbox" checked={grouped} onChange={(e) => setGrouped(e.target.checked)} />
          Group by SO
        </label>
      </div>

      {grouped
        ? <GroupedEblast filters={filters} filterKey={filterKey} />
        : <FlatEblast filters={filters} filterKey={filterKey} />}
    </>
  );
}

// ---- Flat view (one row per drop) ------------------------------------------

const flatColumns: Column<EmailCampaign>[] = [
  { key: 'status', label: 'Status', sortField: 'status', width: '7rem', render: (c) => <StatusPill status={c.status} /> },
  { key: 'so', label: 'SO', sortField: 'so', width: '9rem',
    render: (c) => <code className="type">{c.so || '—'}</code> },
  { key: 'name', label: 'Campaign', sortField: 'name',
    render: (c) => <strong>{stripApiPrefix(c.name) || <span className="muted">(untitled)</span>}</strong> },
  { key: 'client', label: 'Client', width: '11rem', render: (c) => c.client || <span className="muted">—</span> },
  { key: 'records', label: 'Records', align: 'right', sortField: 'total_records', width: '8rem',
    render: (c) => <span className="num">{int(c.totalRecords)}</span> },
  { key: 'interactions', label: 'Interactions', align: 'right', width: '7rem',
    render: (c) => <Stat value={c.totalOpens} rate={pct(c.opensPct)} /> },
  { key: 'clicks', label: 'Clicks', align: 'right', width: '7rem',
    render: (c) => <Stat value={c.totalClicks} rate={pct(c.clicksPct)} /> },
  { key: 'deploy', label: 'Deploy', sortField: 'campaign_start_datetime', width: '7rem',
    render: (c) => <span className="num">{day(c.startDatetime)}</span> },
];

function FlatEblast({ filters, filterKey }: { filters: Filters; filterKey: string }) {
  const navigate = useNavigate();
  const load = useCallback(
    (p: ListParams) => email.campaigns({
      ...p, status: undefined,
      ...filters,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterKey],
  );
  return (
    <DataTable
      title="EBlast Campaigns"
      columns={flatColumns}
      load={load}
      initialSort="campaign_start_datetime"
      initialDirection="desc"
      searchPlaceholder="Search eblast campaigns…"
      onRowClick={(c) => navigate(`/eblast/deploy/${encodeURIComponent(c.id)}`)}
    />
  );
}

// ---- Grouped view (one row per SO, drops in an accordion) -------------------

const GROUP_HEADERS: Array<{ label: string; sort?: string; align?: 'right' }> = [
  { label: 'Status', sort: 'status' },
  { label: 'SO', sort: 'so' },
  { label: 'Campaign', sort: 'name' },
  { label: 'Client' },
  { label: 'Deploys', sort: 'campaign_start_datetime' },
  { label: 'Records', sort: 'total_records', align: 'right' },
  { label: 'Interactions', align: 'right' },
  { label: 'Clicks', align: 'right' },
  { label: '' },
];

function GroupedEblast({ filters, filterKey }: { filters: Filters; filterKey: string }) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('campaign_start_datetime');
  const [direction, setDirection] = useState<'asc' | 'desc'>('desc');
  const [data, setData] = useState<Page<EmailSoGroup> | null>(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => { setPage(1); }, [filterKey, search, sort, direction]);

  useEffect(() => {
    let live = true;
    setLoading(true);
    email.grouped({ ...filters, page, pageSize, search: search || undefined, sort, direction })
      .then((d) => { if (live) setData(d); })
      .catch(() => { if (live) setData(null); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, page, pageSize, search, sort, direction]);

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / pageSize));

  const onSort = (field?: string) => {
    if (!field) return;
    if (sort === field) setDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSort(field); setDirection('desc'); }
  };
  const toggle = (so: string) =>
    setExpanded((s) => { const n = new Set(s); n.has(so) ? n.delete(so) : n.add(so); return n; });

  return (
    <section className="table-block wide">
      <header className="table-head">
        <h2 className="table-title">EBlast Campaigns — by SO</h2>
        <input className="search" placeholder="Search SO / campaign…" value={search}
          onChange={(e) => setSearch(e.target.value)} />
      </header>

      <div className="table-scroll">
        <table className="grouped">
          <thead>
            <tr>
              <th className="expander" />
              {GROUP_HEADERS.map((h, i) => (
                <th key={i} style={{ textAlign: h.align ?? 'left' }}>
                  {h.sort
                    ? <button className="sortable" onClick={() => onSort(h.sort)}>
                        {h.label}{sort === h.sort && <span className="arrow">{direction === 'asc' ? '▲' : '▼'}</span>}
                      </button>
                    : h.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr><td colSpan={10} className="empty">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={10} className="empty">No eblast campaigns match.</td></tr>
            ) : rows.map((g) => {
              const open = expanded.has(g.so);
              const range = g.dropCount > 1 ? `${day(g.firstDeploy)} → ${day(g.lastDeploy)}` : day(g.firstDeploy);
              return (
                <Fragment key={g.so}>
                  <tr className={`group-row${open ? ' open' : ''}`} onClick={() => toggle(g.so)}>
                    <td className="expander"><span className="caret">{open ? '▾' : '▸'}</span></td>
                    <td>
                      <span title={g.status === 'COMPLETED'
                        ? `All ${g.dropCount} deploy${g.dropCount === 1 ? '' : 's'} completed${g.latestEnd ? ` by ${g.latestEnd}` : ''}`
                        : `Across ${g.dropCount} deploy${g.dropCount === 1 ? '' : 's'} — completes only when every deploy has ended`}>
                        <StatusPill status={g.status} />
                      </span>
                    </td>
                    <td><code className="type">{g.so || '—'}</code></td>
                    <td><strong>{stripApiPrefix(g.name) || <span className="muted">(untitled)</span>}</strong></td>
                    <td>{g.client || <span className="muted">—</span>}</td>
                    <td><span className="badge">{g.dropCount}</span> <span className="muted">{range}</span></td>
                    <td style={{ textAlign: 'right' }} className="num">{int(g.totalRecords)}</td>
                    <td style={{ textAlign: 'right' }}><Stat value={g.totalOpens} rate={pct(g.opensPct)} /></td>
                    <td style={{ textAlign: 'right' }}><Stat value={g.totalClicks} rate={pct(g.clicksPct)} /></td>
                    <td style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                      <button className="button sm"
                        onClick={() => navigate(`/eblast/report/${encodeURIComponent(g.reportSo || g.so)}`)}>
                        Report
                      </button>
                    </td>
                  </tr>
                  {open && (
                    <tr className="drops-row">
                      <td />
                      <td colSpan={9}>
                        <table className="drops">
                          <thead>
                            <tr>
                              <th>Deployment</th><th>Deploy date</th><th style={{ textAlign: 'right' }}>Records</th>
                              <th style={{ textAlign: 'right' }}>Interactions</th><th style={{ textAlign: 'right' }}>Clicks</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {g.drops.map((d, i) => (
                              <tr key={d.id} className="drop clickable"
                                onClick={() => navigate(`/eblast/deploy/${encodeURIComponent(d.id)}`)}>
                                <td><span className="drop-no">#{i + 1}</span> {stripApiPrefix(d.name) || <span className="muted">(untitled)</span>}</td>
                                <td className="num">{day(d.deploy)}</td>
                                <td style={{ textAlign: 'right' }} className="num">{int(d.records)}</td>
                                <td style={{ textAlign: 'right' }}><Stat value={d.opens} rate={pct(d.opensPct)} /></td>
                                <td style={{ textAlign: 'right' }}><Stat value={d.clicks} rate={pct(d.clicksPct)} /></td>
                                <td><StatusPill status={d.status} /></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
        {loading && rows.length > 0 && <div className="busy">Loading…</div>}
      </div>

      <footer className="pager">
        <span className="count">{int(total)} SO{total === 1 ? '' : 's'}{total > 0 && ` · page ${page} of ${pages}`}</span>
        <span className="controls">
          <button disabled={page <= 1} onClick={() => setPage(1)}>« First</button>
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>‹ Prev</button>
          <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next ›</button>
        </span>
      </footer>
    </section>
  );
}

// ---- Client (agency) multi-select — same behavior as the RTB spreadsheet ----

function ClientMultiSelect({ options, value, onChange }: {
  options: ClientOption[]; value: string[]; onChange: (ids: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const shown = options.filter((o) => o.name.toLowerCase().includes(filter.trim().toLowerCase()));
  const toggle = (id: string) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  const label = value.length === 0 ? 'All clients' : `${value.length} selected`;

  return (
    <div className="msel" ref={ref}>
      <button type="button" className="button msel-btn" onClick={() => setOpen((o) => !o)} disabled={options.length === 0}>
        Clients: {label} <span className="caret">▾</span>
      </button>
      {open && (
        <div className="msel-panel">
          <input className="msel-filter" placeholder="Filter clients…" value={filter} onChange={(e) => setFilter(e.target.value)} autoFocus />
          <div className="msel-actions">
            <button type="button" className="lnk"
              onClick={() => onChange([...new Set([...value, ...shown.map((o) => o.id)])])}>All</button>
            <button type="button" className="lnk"
              onClick={() => { const s = new Set(shown.map((o) => o.id)); onChange(value.filter((id) => !s.has(id))); }}>None</button>
            <span className="grow" />
            <span className="muted">{value.length}/{options.length}</span>
          </div>
          <div className="msel-list">
            {shown.length === 0 ? <div className="muted msel-empty">No matching clients.</div>
              : shown.map((o) => (
                <label key={o.id} className="msel-item">
                  <input type="checkbox" checked={value.includes(o.id)} onChange={() => toggle(o.id)} />
                  <span>{o.name}</span>
                </label>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}
